/**
 * Input Area Component
 *
 * Orchestrator for the chat input: owns local state (message text,
 * attached files, refs, chip-strip mode), renders the rounded outer
 * container with a textarea on top, an optional wrap-above chip row,
 * and the InputToolbar beneath.
 */

import {
  useState,
  KeyboardEvent,
  ChangeEvent,
  ClipboardEvent,
  useRef,
  useEffect,
} from 'react';
import { cn } from '@/utils/classNames';
import { MESSAGE_LIMITS, IMAGE_EXTENSIONS } from '@/utils/constants';
import { usePreferences } from '@/context/PreferencesContext';
import { useAgent } from '@/context/AgentContext';
import { useAdmin } from '@/context/AdminContext';
import { validatePickedFiles, type FileRejection } from '@/utils/fileValidation';
import { isImageFile } from '@/utils/fileParser';
import { compressImage } from '@/utils/imageCompression';
import { agentSupportsVision } from '@/utils/agentCapabilities';
import { reportError } from '@/services/errorTelemetry';
import { ErrorMessage } from '@/components/Common/ErrorMessage';
import { InputToolbar } from './InputToolbar';
import {
  AttachmentChipStrip,
  type ChipStripMode,
} from './AttachmentChipStrip';

export interface InputAreaProps {
  onSendMessage: (content: string, files?: FileList) => void;
  onFileUpload?: (files: FileList) => void;
  disabled?: boolean;
  /**
   * True while a chat completion is in flight. When set together with
   * `onCancelGeneration`, the Send button flips into a Stop button
   * that cancels the request instead of starting a new one.
   */
  isLoading?: boolean;
  /** Invoked when the user presses Stop. */
  onCancelGeneration?: () => void;
  placeholder?: string;
  className?: string;
}

export function InputArea({
  onSendMessage,
  onFileUpload,
  disabled = false,
  isLoading = false,
  onCancelGeneration,
  placeholder = 'Type a message...',
  className,
}: InputAreaProps) {
  const { preferences, updatePreferences } = usePreferences();
  const { selectedAgent } = useAgent();
  const { config } = useAdmin();
  const visionEnabled = agentSupportsVision(selectedAgent);
  const effectiveUploadLimits = visionEnabled
    ? {
        ...config.uploadLimits,
        allowedExtensions: [
          ...config.uploadLimits.allowedExtensions,
          ...IMAGE_EXTENSIONS,
        ],
      }
    : config.uploadLimits;
  const [message, setMessage] = useState('');
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [chipStripMode, setChipStripMode] = useState<ChipStripMode>('inline');
  const [rejectionBanner, setRejectionBanner] = useState<FileRejection[] | null>(null);
  const [imageHint, setImageHint] = useState<string | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  // Setting `disabled` on the textarea while a completion is in flight
  // makes the browser drop focus, and re-enabling doesn't restore it.
  // This flag lets us refocus once the disabled→enabled transition lands,
  // but only when the user just sent (not on mount or other transitions).
  const refocusAfterSendRef = useRef(false);

  // Auto-resize textarea
  useEffect(() => {
    const textarea = textareaRef.current;
    if (textarea) {
      textarea.style.height = 'auto';
      textarea.style.height = `${textarea.scrollHeight}px`;
    }
  }, [message]);

  useEffect(() => {
    if (!disabled && refocusAfterSendRef.current) {
      refocusAfterSendRef.current = false;
      textareaRef.current?.focus();
    }
  }, [disabled]);

  // When files are cleared, reset chip mode so the next attach starts
  // in inline mode rather than inheriting a stale wrap-above flip.
  useEffect(() => {
    if (selectedFiles.length === 0) {
      setChipStripMode('inline');
    }
  }, [selectedFiles.length]);

  // Auto-dismiss rejection banner after 8 seconds.
  useEffect(() => {
    if (!rejectionBanner) return;
    const timer = setTimeout(() => setRejectionBanner(null), 8000);
    return () => clearTimeout(timer);
  }, [rejectionBanner]);

  // Auto-dismiss the non-vision image hint after 4 seconds.
  useEffect(() => {
    if (!imageHint) return;
    const timer = setTimeout(() => setImageHint(null), 4000);
    return () => clearTimeout(timer);
  }, [imageHint]);

  const handleSend = () => {
    const trimmed = message.trim();
    if (trimmed && !disabled) {
      const fileList =
        selectedFiles.length > 0 ? createFileList(selectedFiles) : undefined;
      onSendMessage(trimmed, fileList);
      setMessage('');
      // Clear the attachment once it's been sent so it isn't silently reused
      // on the next turn — unless the user opted to keep files across
      // messages. The fileList snapshot above already carried the files into
      // this turn's context, so clearing here doesn't affect what was sent.
      if (!preferences.keepFilesAcrossMessages) {
        setSelectedFiles([]);
      }
      if (textareaRef.current) {
        textareaRef.current.style.height = 'auto';
      }
      refocusAfterSendRef.current = true;
    }
  };

  const createFileList = (files: File[]): FileList => {
    const dataTransfer = new DataTransfer();
    files.forEach((file) => dataTransfer.items.add(file));
    return dataTransfer.files;
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey && preferences.sendOnEnter) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleChange = (e: ChangeEvent<HTMLTextAreaElement>) => {
    const value = e.target.value;
    if (value.length <= MESSAGE_LIMITS.MAX_CONTENT_LENGTH) {
      setMessage(value);
    }
  };

  const handleFileClick = () => {
    fileInputRef.current?.click();
  };

  // Shared attach path for both the file picker and pasted files. Images are
  // compressed BEFORE validation so large screenshots survive the size check
  // and the stored base64 stays small.
  const addFiles = async (incoming: File[]) => {
    // Images need a vision-capable agent. On a non-vision agent, drop them and
    // show a friendly hint instead of a generic rejection banner; non-image
    // files still attach. Centralized here so paste and the paperclip behave
    // identically.
    let candidates = incoming;
    if (!visionEnabled && incoming.some((f) => isImageFile(f))) {
      setImageHint(
        "This agent can't read images, so the image wasn't attached.",
      );
      candidates = incoming.filter((f) => !isImageFile(f));
    }
    if (candidates.length === 0) return;

    const processed = await Promise.all(
      candidates.map((f) =>
        isImageFile(f) ? compressImage(f) : Promise.resolve(f),
      ),
    );

    const { valid, rejections } = validatePickedFiles(processed, {
      alreadyAttached: selectedFiles.length,
      limits: effectiveUploadLimits,
    });

    if (valid.length > 0) {
      setSelectedFiles((prev) => [...prev, ...valid]);
      if (onFileUpload) {
        const dt = new DataTransfer();
        valid.forEach((f) => dt.items.add(f));
        onFileUpload(dt.files);
      }
    }

    if (rejections.length > 0) {
      setRejectionBanner(rejections);
      rejections.forEach((r) => {
        reportError(
          `File rejected: ${r.reason}`,
          {
            area: 'file-attach',
            fileName: r.file.name,
            fileSize: r.file.size,
            fileType: r.file.type || 'unknown',
            rejectionReason: r.code,
            effectiveMaxMB: effectiveUploadLimits.maxFileSizeMB,
            effectiveMaxFiles: effectiveUploadLimits.maxFiles,
          },
          'warning',
        );
      });
    } else {
      setRejectionBanner(null);
    }
  };

  const handlePaste = (e: ClipboardEvent<HTMLTextAreaElement>) => {
    const pastedFiles = Array.from(e.clipboardData?.items ?? [])
      .filter((it) => it.kind === 'file')
      .map((it, i) => {
        const file = it.getAsFile();
        if (!file) return null;
        // Classify images by MIME as well as extension: a clipboard image
        // blob sometimes has no usable filename. Give a nameless image a
        // synthetic `.png` name so it classifies as an image downstream
        // (compression renames it to `.webp` regardless).
        const isImage = it.type.startsWith('image/') || isImageFile(file);
        return isImage && !file.name
          ? new File([file], `screenshot-${i + 1}.png`, { type: file.type })
          : file;
      })
      .filter((f): f is File => f != null);

    // No files in the clipboard — let text paste normally.
    if (pastedFiles.length === 0) return;

    // We are handling the file(s) ourselves; stop the default paste. The
    // shared attach path applies the vision gate + non-vision hint.
    e.preventDefault();
    void addFiles(pastedFiles);
  };

  const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    const picked = Array.from(e.target.files ?? []);
    if (fileInputRef.current) fileInputRef.current.value = '';
    if (picked.length === 0) return;
    void addFiles(picked);
  };

  const handleRemoveFile = (index: number) => {
    setSelectedFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleToggleTokenUsage = () => {
    updatePreferences({
      showTokenUsage: !preferences.showTokenUsage,
    });
  };

  const charCount = message.length;
  const isAtLimit = charCount >= MESSAGE_LIMITS.MAX_CONTENT_LENGTH;
  const canAttach = !!onFileUpload && preferences.enableFileUpload;

  return (
    <div
      className={cn(
        'border-t border-border-default bg-bg-primary p-4',
        className
      )}
    >
      <div className="mx-auto w-full" data-testid="input-column">
        {imageHint && (
          <ErrorMessage
            className="mb-2"
            severity="info"
            dismissible
            onDismiss={() => setImageHint(null)}
            message={imageHint}
          />
        )}
        {rejectionBanner && rejectionBanner.length > 0 && (
          <ErrorMessage
            className="mb-2"
            severity="warning"
            dismissible
            onDismiss={() => setRejectionBanner(null)}
            message={
              rejectionBanner.length === 1
                ? `Couldn't attach ${rejectionBanner[0].file.name}: ${rejectionBanner[0].reason}.`
                : `Couldn't attach ${rejectionBanner.length} files: ${rejectionBanner
                    .map((r) => `${r.file.name} (${r.reason})`)
                    .join(', ')}.`
            }
          />
        )}
        <div
          className={cn(
            'overflow-hidden rounded-2xl border border-border-default bg-bg-primary',
            'transition-all duration-base',
            'focus-within:border-stellar-primary',
            'focus-within:shadow-[var(--shadow-focus)]'
          )}
        >
          {/* Textarea (top row) */}
          <textarea
            ref={textareaRef}
            value={message}
            onChange={handleChange}
            onKeyDown={handleKeyDown}
            onPaste={handlePaste}
            placeholder={placeholder}
            disabled={disabled}
            rows={1}
            style={{ fieldSizing: 'content' } as React.CSSProperties}
            className={cn(
              'block w-full resize-none border-0 bg-transparent',
              'px-4 pt-2 pb-0.5',
              'text-base text-text-primary',
              'placeholder:text-text-tertiary',
              'focus:outline-none focus:ring-0',
              'max-h-32 overflow-y-auto scrollbar-thin',
              'disabled:cursor-not-allowed disabled:opacity-50'
            )}
            aria-label="Message input"
          />

          {/* Wrap-above chip row (only when chips overflow inline) */}
          {chipStripMode === 'wrap-above' && selectedFiles.length > 0 && (
            <div className="px-3 pb-2">
              <AttachmentChipStrip
                files={selectedFiles}
                onRemove={handleRemoveFile}
                mode="wrap-above"
                onModeChange={setChipStripMode}
              />
            </div>
          )}

          {/* Toolbar (bottom row) */}
          <InputToolbar
            canAttach={canAttach}
            onAttachClick={handleFileClick}
            selectedAgent={selectedAgent ?? undefined}
            showTokenUsage={preferences.showTokenUsage}
            onToggleTokenUsage={handleToggleTokenUsage}
            files={selectedFiles}
            onRemoveFile={handleRemoveFile}
            chipStripMode={chipStripMode}
            onChipStripModeChange={setChipStripMode}
            charCount={charCount}
            charLimit={MESSAGE_LIMITS.MAX_CONTENT_LENGTH}
            disabled={disabled}
            canSend={!disabled && message.trim().length > 0 && !isAtLimit}
            onSend={handleSend}
            isLoading={isLoading}
            onCancel={onCancelGeneration}
          />
        </div>

        {/* Hidden file input — Paperclip click triggers this */}
        {canAttach && (
          <input
            ref={fileInputRef}
            type="file"
            multiple
            onChange={handleFileChange}
            className="hidden"
            accept={effectiveUploadLimits.allowedExtensions.join(',')}
          />
        )}
      </div>
    </div>
  );
}
